import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./tooltip-layer.css";

type Side = "top" | "bottom" | "left" | "right";
type Anchor = { el: Element; text: string; side: Side };

const SHOW_DELAY_MS = 150;
const GAP = 8;
const MARGIN = 8;
const SELECTOR = "[data-tooltip]";
// A frame's title names it for assistive tech and never draws a bubble.
const KEEPS_TITLE = new Set(["IFRAME"]);

// A title cannot be restyled, so its text moves to data-tooltip and the
// title is left empty rather than removed: an empty title draws nothing, and
// React still finds the attribute it set, so when it later drops the prop the
// removal is a mutation the observer can follow. This happens the moment an
// element appears, long before any pointer reaches it.
function adopt(el: Element) {
  if (KEEPS_TITLE.has(el.tagName)) return;
  const title = el.getAttribute("title");
  if (title === null || title === "") return;
  el.setAttribute("data-tooltip", title);
  el.setAttribute("title", "");
  // A control named by its title alone, an icon button, keeps a name.
  if (
    !el.hasAttribute("aria-label") &&
    !el.hasAttribute("aria-labelledby") &&
    !(el.textContent ?? "").trim()
  ) {
    el.setAttribute("aria-label", title);
    el.setAttribute("data-tooltip-named", "");
  }
}

function release(el: Element) {
  el.removeAttribute("data-tooltip");
  if (el.hasAttribute("data-tooltip-named")) {
    el.removeAttribute("aria-label");
    el.removeAttribute("data-tooltip-named");
  }
}

function adoptTree(node: Node) {
  if (!(node instanceof Element)) return;
  if (node.hasAttribute("title")) adopt(node);
  for (const el of node.querySelectorAll("[title]")) adopt(el);
}

function watchTitles() {
  adoptTree(document.body);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "childList") record.addedNodes.forEach(adoptTree);
      else if (record.target instanceof Element) {
        if (record.target.hasAttribute("title")) adopt(record.target);
        else release(record.target);
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["title"],
  });
  return () => observer.disconnect();
}

function anchorFor(target: EventTarget | null): Anchor | null {
  const el = target instanceof Element ? target.closest(SELECTOR) : null;
  const text = el?.getAttribute("data-tooltip");
  if (!el || !text) return null;
  const side = el.getAttribute("data-tooltip-side") as Side | null;
  return { el, text, side: side ?? "top" };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function place(anchor: Anchor, node: HTMLElement) {
  const a = anchor.el.getBoundingClientRect();
  const b = node.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;
  let side = anchor.side;
  if (side === "top" && a.top - b.height - GAP < MARGIN) side = "bottom";
  if (side === "bottom" && a.bottom + b.height + GAP > innerHeight - MARGIN)
    side = "top";
  if (side === "right" && a.right + b.width + GAP > innerWidth - MARGIN)
    side = "left";
  if (side === "left" && a.left - b.width - GAP < MARGIN) side = "right";
  const centerX = a.left + a.width / 2 - b.width / 2;
  const centerY = a.top + a.height / 2 - b.height / 2;
  const x =
    side === "right"
      ? a.right + GAP
      : side === "left"
        ? a.left - b.width - GAP
        : centerX;
  const y =
    side === "bottom"
      ? a.bottom + GAP
      : side === "top"
        ? a.top - b.height - GAP
        : centerY;
  node.style.left = `${clamp(x, MARGIN, innerWidth - b.width - MARGIN)}px`;
  node.style.top = `${clamp(y, MARGIN, innerHeight - b.height - MARGIN)}px`;
  node.dataset.side = side;
}

/**
 * One instance at the root turns every `title` in the app into the styled
 * tooltip. An element can also carry `data-tooltip` itself, and
 * `data-tooltip-side="right"` (or left, bottom) when above it is the wrong
 * place, as on the collapsed sidebar rail.
 */
export function TooltipLayer() {
  const [shown, setShown] = useState<Anchor | null>(null);
  const node = useRef<HTMLDivElement>(null);

  useEffect(watchTitles, []);

  useEffect(() => {
    let pending: Anchor | null = null;
    let lastTarget: EventTarget | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Focus that follows a click is not a request for the tooltip; focus
    // reached from the keyboard is, since there is no pointer to hover with.
    let keyboard = false;

    const hide = () => {
      clearTimeout(timer);
      pending = null;
      setShown(null);
    };
    const show = (anchor: Anchor, delay: number) => {
      clearTimeout(timer);
      pending = anchor;
      timer = setTimeout(() => setShown(anchor), delay);
    };
    // Over and move both land here: a row React redraws under a resting
    // pointer is a new element, and the move is what notices it.
    const track = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.target === lastTarget) return;
      lastTarget = event.target;
      const anchor = anchorFor(event.target);
      if (!anchor) {
        if (pending) hide();
        return;
      }
      if (pending?.el === anchor.el && anchor.el.isConnected) return;
      show(anchor, pending ? 0 : SHOW_DELAY_MS);
    };
    const onPointerOut = (event: PointerEvent) => {
      const related = event.relatedTarget;
      if (
        pending &&
        !(related instanceof Node && pending.el.contains(related))
      ) {
        lastTarget = null;
        hide();
      }
    };
    const onPointerDown = () => {
      keyboard = false;
      hide();
    };
    const onFocusIn = (event: FocusEvent) => {
      const anchor = anchorFor(event.target);
      if (anchor && keyboard) show(anchor, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      keyboard = true;
      if (event.key === "Escape") hide();
    };

    document.addEventListener("pointerover", track);
    document.addEventListener("pointermove", track);
    document.addEventListener("pointerout", onPointerOut);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", hide);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("scroll", hide, true);
    return () => {
      hide();
      document.removeEventListener("pointerover", track);
      document.removeEventListener("pointermove", track);
      document.removeEventListener("pointerout", onPointerOut);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("scroll", hide, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (shown && node.current) place(shown, node.current);
  }, [shown]);

  if (!shown) return null;
  return (
    <div role="tooltip" className="app-tooltip" ref={node}>
      {shown.text}
    </div>
  );
}
